<template>
  <div class="auth-page">
    <div class="top-bar">
      <LangSwitch />
    </div>

    <div class="brand">
      <div class="logo"></div>
      <div class="brand-name">Tiempos</div>
    </div>

    <div class="card">
      <div class="card-title">{{ $t('login.submit') }}</div>

      <van-form @submit="onSubmit">
        <div class="field-wrap">
          <div class="field-label">{{ $t('login.user') }}</div>
          <van-field
            v-model="mobile"
            name="mobile"
            type="tel"
            :placeholder="$t('login.userPlaceholder')"
            :rules="[
              { required: true, message: $t('login.userPlaceholder') },
              { pattern: /^\d{8}$/, message: $t('login.userMustBe8Digits'), trigger: 'onBlur' }
            ]"
            :border="false"
          />
        </div>

        <div class="field-wrap">
          <div class="field-label">{{ $t('login.password') }}</div>
          <van-field
            v-model="password"
            type="password"
            name="password"
            :placeholder="$t('login.passwordPlaceholder')"
            :rules="[{ required: true, message: $t('login.passwordPlaceholder') }]"
            :border="false"
          />
        </div>

        <van-button round block native-type="submit" class="submit-btn"> {{ $t('login.submit') }} </van-button>
      </van-form>

      <div class="footer-link" @click="register">
        {{ $t('login.createAccount') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Toast } from 'vant';
import { useI18n } from 'vue-i18n';
import { authToken, authMobile, authId } from '@/constants';
import { storage } from '@/utils';
import { useStore } from 'vuex';
import { LoginService } from '@/services';
import LangSwitch from '@/components/LangSwitch.vue';

const { t } = useI18n();

const mobile = ref('');
const password = ref('');
const store = useStore();
const router = useRouter();

// Toast 冷却:2 秒内最多弹一次,避免用户长按/连按时刷屏
let lastToastAt = 0;
const showMaxDigitToast = () => {
  const now = Date.now();
  if (now - lastToastAt > 2000) {
    Toast.fail(t('login.userMustBe8Digits'));
    lastToastAt = now;
  }
};

// 监听 mobile 变化:去非数字 + 截 8 位,超 8 位马上弹提示
// (Vant 3 的 van-field 不触发原生 input 事件,只能通过 watch v-model 的 ref 实现)
watch(mobile, (val) => {
  const raw = String(val || '').replace(/\D/g, '');
  const truncated = raw.slice(0, 8);
  if (truncated !== val) mobile.value = truncated;
  if (raw.length > 8) showMaxDigitToast();
});

const onSubmit = async (values) => {
  try {
    // 8 位数字二次校验,防绕过前端
    if (!/^\d{8}$/.test(values.mobile || '')) {
      Toast.fail(t('login.userMustBe8Digits'));
      return;
    }
    const { result } = await LoginService.loginApi(values);
    if (result) {
      storage.setItem(authToken, result.token);
      storage.setItem(authMobile, result.mobile);
      storage.setItem(authId, result.id);
      await store.dispatch('user/loginApi', result);
      window.location.reload();
      router.push('/');
    }
  } catch (e) {
    console.log('登录失败');
  }
};

const register = () => {
  router.push('/register');
};
</script>

<style lang="scss" scoped>
.auth-page {
  min-height: 100vh;
  background: linear-gradient(160deg, #667eea 0%, #764ba2 100%);
  padding: 40px 32px 60px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

// 背景光晕
.auth-page::before,
.auth-page::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  pointer-events: none;
}
.auth-page::before {
  width: 400px;
  height: 400px;
  top: -140px;
  right: -80px;
}
.auth-page::after {
  width: 300px;
  height: 300px;
  bottom: -100px;
  left: -60px;
}

.top-bar {
  position: absolute;
  top: 30px;
  right: 30px;
  z-index: 2;
  :deep(.lang-text) {
    color: #fff;
  }
  :deep(.lang-icon) {
    color: #fff;
  }
}

.brand {
  text-align: center;
  margin: 60px 0 40px;
  z-index: 1;
  .logo {
    width: 140px;
    height: 140px;
    background-image: url('./../../assets/images/logo.png');
    background-size: cover;
    background-position: center;
    border-radius: 32px;
    margin: 0 auto 24px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
  }
  .brand-name {
    font-size: 44px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 2px;
  }
}

.card {
  width: 100%;
  background: #fff;
  border-radius: 24px;
  padding: 44px 32px 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  z-index: 1;

  .card-title {
    font-size: 44px;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 32px;
    text-align: center;
    letter-spacing: 2px;
  }
}

.field-wrap {
  margin-bottom: 20px;
  .field-label {
    font-size: 26px;
    color: #6b7280;
    margin-bottom: 10px;
    font-weight: 500;
  }
  :deep(.van-cell) {
    background: #f5f7fa;
    border-radius: 16px;
    padding: 24px 28px;
    font-size: 30px;
    &::after {
      display: none;
    }
  }
  :deep(.van-field__control) {
    color: #1f2937;
    font-size: 30px;
    &::placeholder {
      color: #a0aec0;
    }
  }
}

.submit-btn {
  height: 96px;
  margin-top: 32px;
  background: linear-gradient(135deg, #667eea, #764ba2) !important;
  color: #fff !important;
  border: none !important;
  font-size: 32px !important;
  font-weight: 600;
  letter-spacing: 4px;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.35);
}

.footer-link {
  text-align: center;
  margin-top: 28px;
  color: #667eea;
  font-size: 26px;
  font-weight: 500;
  padding: 12px;
  cursor: pointer;
}
</style>
